import type { ChatMessage, ReportReason } from "@rhema/client";
import { Button, cn, focusRing, Sheet } from "@rhema/ui";
import { type FormEvent, useEffect, useId, useMemo, useState } from "react";
import { useApi, useClientState, useMe } from "../session/session";

const REASONS: { value: ReportReason; label: string }[] = [
  { value: "spam", label: "Spam or scams" },
  { value: "harassment", label: "Harassment or bullying" },
  { value: "threat", label: "Threats of violence" },
  { value: "impersonation", label: "Pretending to be someone else" },
  { value: "illegal", label: "Something illegal" },
  { value: "other", label: "Something else" },
];

/** How many recent messages a report can carry. The server takes at most 10. */
const SHARED = 10;

export type ReportTarget = {
  /** The person reported. Left out for a group. */
  userId?: string;
  /** The chat the report is about, whose recent messages can go with it. */
  chatId?: string;
  name: string;
  /** Report message: the one message reported, as this device shows it. */
  message?: { id: string; text: string };
};

/**
 * Reports a person or a group to SiliconBlast, as the App Store and Google Play ask of apps where
 * people send each other content. Messages are end-to-end encrypted, so SiliconBlast sees only the
 * messages the person chooses to include, decrypted on this device.
 */
export function ReportSheet({
  open,
  onClose,
  target,
}: {
  open: boolean;
  onClose: (sent: boolean) => void;
  target: ReportTarget;
}) {
  const api = useApi();
  const me = useMe();
  const state = useClientState();
  const noteId = useId();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [note, setNote] = useState("");
  const [share, setShare] = useState(true);
  // Report message sends the message's text only with the person's say-so, which starts ticked.
  const [consent, setConsent] = useState(true);
  const [block, setBlock] = useState(Boolean(target.userId));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Each report starts with recent messages included and, for a person, blocking ticked.
  useEffect(() => {
    if (!open) return;
    setShare(true);
    setConsent(true);
    setBlock(Boolean(target.userId));
  }, [open, target.userId]);

  // The latest text messages from the people reported, never the reporter's own.
  const recent = useMemo(() => {
    if (target.message) return [];
    const list: ChatMessage[] = target.chatId ? (state.messages[target.chatId] ?? []) : [];
    return list
      .filter((m) => m.kind === "message" && !m.deleted && m.senderId !== me.id)
      .filter((m) => !target.userId || m.senderId === target.userId)
      .filter((m) => m.content?.type === "text" && m.content.body.trim())
      .slice(-SHARED);
  }, [state.messages, target.chatId, target.userId, target.message, me.id]);

  const close = (sent: boolean) => {
    setReason(null);
    setNote("");
    setError(null);
    onClose(sent);
  };

  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!reason) return;
    setBusy(true);
    setError(null);
    try {
      await api.report({
        ...(target.userId ? { user_id: target.userId } : {}),
        ...(target.chatId ? { chat_id: target.chatId } : {}),
        reason,
        note: note.trim(),
        messages: share
          ? recent.map((m) => ({
              message_id: m.id,
              sender_id: m.senderId,
              text: m.content?.type === "text" ? m.content.body.slice(0, 4000) : "",
              sent_at: m.createdAt,
            }))
          : [],
        block: Boolean(target.userId) && block,
        ...(target.message && consent
          ? { message_id: target.message.id, message_text: target.message.text.slice(0, 4000) }
          : {}),
      });
      close(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={() => close(false)}
      title={target.message ? "Report message" : `Report ${target.name}`}
      subtitle={target.message ? `From ${target.name}` : undefined}
    >
      <form onSubmit={send} className="mt-3 flex flex-col gap-3">
        <p className="m-0 text-[13.5px] leading-normal text-ink-secondary">
          We review every report. Messages are end-to-end encrypted, so we see only the messages you choose to
          include.
        </p>
        <fieldset className="m-0 flex flex-col gap-1 border-0 p-0">
          <legend className="mb-1 p-0 text-[13px] font-semibold text-ink">What is wrong?</legend>
          {REASONS.map((r) => (
            <label
              key={r.value}
              className="flex cursor-pointer items-center gap-2.5 rounded-field px-2 py-2 text-[15px] hover:bg-surface-header"
            >
              <input
                type="radio"
                name="report-reason"
                value={r.value}
                checked={reason === r.value}
                onChange={() => setReason(r.value)}
              />
              {r.label}
            </label>
          ))}
        </fieldset>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={noteId} className="text-[13px] font-semibold text-ink">
            Anything else we should know (optional)
          </label>
          <textarea
            id={noteId}
            rows={3}
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={cn(
              "rounded-field border border-line bg-surface px-4 py-3 font-sans text-[15px] text-ink focus:border-blue",
              focusRing,
            )}
          />
        </div>
        {target.message && (
          <>
            <blockquote className="m-0 max-h-32 overflow-y-auto rounded-field bg-surface-header px-3.5 py-2.5 text-[14px] whitespace-pre-wrap text-ink [overflow-wrap:anywhere]">
              {target.message.text}
            </blockquote>
            <label className="flex cursor-pointer items-start gap-2.5 text-[15px]">
              <input
                type="checkbox"
                className="mt-1"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <span>Send this message to review with my report</span>
            </label>
          </>
        )}
        {recent.length > 0 && (
          <label className="flex cursor-pointer items-start gap-2.5 text-[15px]">
            <input
              type="checkbox"
              className="mt-1"
              checked={share}
              onChange={(e) => setShare(e.target.checked)}
            />
            <span>
              Include the last {recent.length === 1 ? "message" : `${recent.length} messages`} from{" "}
              {target.name}
            </span>
          </label>
        )}
        {target.userId && (
          <label className="flex cursor-pointer items-start gap-2.5 text-[15px]">
            <input
              type="checkbox"
              className="mt-1"
              checked={block}
              onChange={(e) => setBlock(e.target.checked)}
            />
            <span>Also block {target.name}</span>
          </label>
        )}
        {error && (
          <p role="alert" className="m-0 text-sm font-semibold text-scarlet">
            {error}
          </p>
        )}
        <div className="flex gap-2">
          <Button type="submit" variant="danger" disabled={!reason || busy}>
            {busy ? "Sending" : "Send report"}
          </Button>
          <Button variant="ghost" disabled={busy} onClick={() => close(false)}>
            Cancel
          </Button>
        </div>
      </form>
    </Sheet>
  );
}
